import React, { useEffect } from 'react';
import { nanoid } from 'nanoid';
import { Experience } from '@mrkt/features/experience-definitions/helpers';
import { logApdexMetric } from '../apdex';

type Init = {
  view: string;
  experience: Experience;
  target?: number;
};

export function createLoadingHook({ view, experience, target = 500 }: Init) {
  return function useLoading(loading = false) {
    useEffect(() => {
      if (!loading) return noop;
      const start = Date.now();

      return () => {
        const duration = Date.now() - start;
        logApdexMetric('loading', duration, target, {
          experience: experience.id,
          view,
          owner: experience.owner,
        });
      };
    }, [loading]);

    useEffect(() => {
      if (!loading || process.env.NODE_ENV !== 'development') return noop;
      const startMark = nanoid();
      const measureName = `⚙️ Loading [${experience.id}.${view}]`;
      performance.mark(startMark);

      return () => {
        performance.measure(measureName, startMark);
        performance.clearMarks(startMark);
        performance.clearMeasures(measureName);
      };
    }, [loading]);
  };
}

export function createLoadingBoundary(init: Init) {
  const useLoading = createLoadingHook(init);

  function Loading({ children }: { children: React.ReactNode }) {
    useLoading(true);
    return <>{children}</>;
  }

  return function LoadingBoundary({ children, fallback }: React.SuspenseProps) {
    return (
      <React.Suspense fallback={fallback && <Loading>{fallback}</Loading>}>
        {children}
      </React.Suspense>
    );
  };
}

function noop() {}
